import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import { TextLines } from "@/components/tool-kit/diagrams/svg-parts";
import {
  type BlockLayout,
  CANVAS_HEIGHT,
  CANVAS_TEXT,
  CANVAS_WIDTH,
  type CanvasBlock,
  FOOTER_TOP,
} from "@/lib/tool-kit/business-templates/canvas";
import { CHART_INK } from "@/lib/tool-kit/palette";

export interface CanvasHeaderText {
  title: string;
  /** "Designed for: …" and so on; blank entries are left out. */
  details: readonly string[];
}

/**
 * The Business Model Canvas as a self-contained SVG. The nine boxes follow the layout of
 * Strategyzer's published canvas; the attribution lines the licence asks for are part of
 * the drawing, so they travel with every exported SVG and PNG.
 */
export function CanvasDiagram({
  id,
  layouts,
  names,
  header,
  footer,
  label,
  description,
}: {
  id: string;
  layouts: readonly BlockLayout[];
  names: Record<CanvasBlock, string>;
  header: CanvasHeaderText;
  footer: readonly string[];
  label: string;
  description: string;
}) {
  return (
    <ChartSvg
      description={description}
      height={CANVAS_HEIGHT}
      id={id}
      label={label}
      width={CANVAS_WIDTH}
    >
      <TextLines
        anchor="start"
        bold
        fontSize={24}
        lineHeight={30}
        lines={[header.title]}
        top={16}
        x={20}
      />
      <TextLines
        anchor="start"
        fill={CHART_INK.muted}
        fontSize={12}
        lineHeight={16}
        lines={header.details}
        top={52}
        x={20}
      />
      {layouts.map(({ block, rect, lines }) => (
        <g key={block}>
          <rect
            fill="#ffffff"
            height={rect.height}
            stroke={CHART_INK.text}
            strokeWidth={2}
            width={rect.width}
            x={rect.x}
            y={rect.y}
          />
          <TextLines
            anchor="start"
            bold
            fontSize={15}
            lineHeight={20}
            lines={[names[block]]}
            top={rect.y + CANVAS_TEXT.pad - 2}
            x={rect.x + CANVAS_TEXT.pad}
          />
          {lines.map((line) => (
            <g key={`${line.top}-${line.text}`}>
              {line.bullet ? (
                <TextLines
                  anchor="start"
                  fontSize={CANVAS_TEXT.fontSize}
                  lineHeight={CANVAS_TEXT.lineHeight}
                  lines={["•"]}
                  top={rect.y + line.top}
                  x={rect.x + CANVAS_TEXT.pad}
                />
              ) : null}
              <TextLines
                anchor="start"
                fontSize={CANVAS_TEXT.fontSize}
                lineHeight={CANVAS_TEXT.lineHeight}
                lines={[line.text]}
                top={rect.y + line.top}
                x={rect.x + CANVAS_TEXT.pad + CANVAS_TEXT.bulletIndent}
              />
            </g>
          ))}
        </g>
      ))}
      <TextLines
        anchor="start"
        fill={CHART_INK.muted}
        fontSize={11}
        lineHeight={14}
        lines={footer}
        top={FOOTER_TOP}
        x={20}
      />
    </ChartSvg>
  );
}
